import { z } from 'zod';
import {
    MAX_GROUPS_PER_SERVICE,
    MAX_OPTIONS_PER_GROUP,
} from '../types/serviceOptionGroups';

/**
 * PBI 1962 (AB#1968) — client-side Zod schema for the service option-groups
 * builder. Mirrors the backend `ServiceOptionGroupRules` validators
 * (backend-handoff-phase2.md) so the owner gets fast, actionable feedback;
 * the backend remains authoritative.
 *
 * Enforced rules (Faris §1.3 / §10):
 *   - EXACTLY ONE Variant group per service (v1).
 *   - ≤ 8 groups / service, ≤ 20 options / group.
 *   - required ≡ minSelect >= 1 (derived, not a separate flag).
 *   - Kind ↔ SelectionType consistency:
 *       Variant  ⇒ Single, min = max = 1
 *       AddOn    ⇒ Multiple
 *   - min ≤ max; each group has ≥ 1 option.
 *   - Single currency across all options of the service.
 *
 * Error strings are i18n KEYS (resolved via useLanguage().t) so both EN and AR
 * render. The builder maps a failed path back to the offending group/option.
 */

const optionRowSchema = z.object({
    key: z.string(),
    id: z.number().optional(),
    name: z.string().trim().min(1, 'services.builder.error.optionNameRequired'),
    nameAr: z.string().trim().optional().default(''),
    price: z
        .number({ invalid_type_error: 'services.builder.error.priceNonNegative' })
        .min(0, 'services.builder.error.priceNonNegative'),
    durationMin: z
        .number({ invalid_type_error: 'services.builder.error.durationNonNegative' })
        .min(0, 'services.builder.error.durationNonNegative'),
});

const optionGroupSchema = z
    .object({
        key: z.string(),
        id: z.number().optional(),
        name: z.string().trim().min(1, 'services.builder.error.groupNameRequired'),
        nameAr: z.string().trim().optional().default(''),
        kind: z.enum(['Variant', 'AddOn']),
        selectionType: z.enum(['Single', 'Multiple']),
        minSelect: z.number().int().min(0),
        maxSelect: z.number().int().min(1),
        isImplicit: z.boolean(),
        options: z
            .array(optionRowSchema)
            .min(1, 'services.builder.error.maxOneOption')
            .max(MAX_OPTIONS_PER_GROUP, 'services.builder.error.tooManyOptions'),
    })
    .superRefine((group, ctx) => {
        // min ≤ max
        if (group.minSelect > group.maxSelect) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'services.builder.error.minMax',
                path: ['maxSelect'],
            });
        }
        // Kind ↔ SelectionType consistency
        if (group.kind === 'Variant') {
            // A Variant group is the single/absolute base selector: Single, 1..1.
            if (group.selectionType !== 'Single') {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'services.builder.error.minMax',
                    path: ['selectionType'],
                });
            }
        }
    });

export const serviceOptionGroupsSchema = z
    .array(optionGroupSchema)
    .max(MAX_GROUPS_PER_SERVICE, 'services.builder.error.tooManyGroups')
    .superRefine((groups, ctx) => {
        // Exactly one Variant group per service (v1).
        const variantCount = groups.filter((g) => g.kind === 'Variant').length;
        if (groups.length > 0 && variantCount !== 1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'services.builder.error.oneVariant',
                path: [],
            });
        }
        // Single currency is enforced at the form level (the currency selector is
        // service-wide, so per-option currency can never diverge in the UI); this
        // is a defensive assertion for programmatic callers only.
    });

export type ServiceOptionGroupsForm = z.infer<typeof serviceOptionGroupsSchema>;
